import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Clock, Dumbbell, AlertTriangle, ArrowLeft, Brain, ShieldAlert,
  Wrench, MessageSquare, Pencil, Loader2, Check, X, ToggleLeft,
  Copy, FolderPlus,
} from "lucide-react";
import { parseFactReferences, renderFactSegments } from "@/lib/factReferences";
import { SessionAdviceBlock } from "@/components/sessions/SessionAdviceBlock";
import { FactText } from "@/components/facts/FactText";
import { StartSessionButton } from "@/components/sessions/StartSessionButton";
import { SessionCalendarButton } from "@/components/sessions/SessionCalendarButton";
import { AddToProgramDialog } from "@/components/programs/AddToProgramDialog";
import { DuplicateSessionDialog } from "@/components/programs/DuplicateSessionDialog";
import { useInterfaceMode } from "@/hooks/useInterfaceMode";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSessions } from "@/hooks/useSessions";
import type { SessionRecord, SessionExercise } from "@/hooks/useSessions";

interface SessionViewerProps {
  session: SessionRecord;
  onBack: () => void;
}

type AdaptMode = null | "choose" | "manual" | "ai";

export function SessionViewer({ session, onBack }: SessionViewerProps) {
  const { mode } = useInterfaceMode();
  const isSimple = mode === "simple";
  const { duplicateSession } = useSessions();
  const [adaptMode, setAdaptMode] = useState<AdaptMode>(null);
  const [constraint, setConstraint] = useState("");
  const [isPermanent, setIsPermanent] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [adaptedExercises, setAdaptedExercises] = useState<SessionExercise[] | null>(null);
  const [adaptSummary, setAdaptSummary] = useState<string | null>(null);
  const [manualExercises, setManualExercises] = useState<SessionExercise[]>([]);
  const [addToProgramOpen, setAddToProgramOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  // Use adapted exercises if available, otherwise original
  const displayExercises = adaptedExercises || session.exercises || [];

  const handleAdaptAI = async () => {
    if (!constraint.trim()) {
      toast.error("Décris ta contrainte");
      return;
    }
    setAiLoading(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;
      if (!token) {
        toast.error("Connexion requise");
        setAiLoading(false);
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/adapt-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session: {
              title: session.title,
              duration_minutes: session.duration_minutes,
              level_required: session.level_required,
              objective: session.objective,
              exercises: session.exercises,
            },
            constraint: constraint.trim(),
            is_permanent: isPermanent,
          }),
        }
      );

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Erreur" }));
        toast.error(err.error || `Erreur ${resp.status}`);
        setAiLoading(false);
        return;
      }

      const data = await resp.json();
      if (data.exercises) {
        setAdaptedExercises(data.exercises);
        setAdaptSummary(data.summary || null);
        setAdaptMode(null);
        toast.success("Séance adaptée !");
      } else {
        toast.error("Réponse du Cerveau invalide");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur de connexion");
    }
    setAiLoading(false);
  };

  const startManualEdit = () => {
    setManualExercises(
      (session.exercises || []).map((ex) => ({ ...ex }))
    );
    setAdaptMode("manual");
  };

  const updateManualExercise = (index: number, field: keyof SessionExercise, value: string) => {
    setManualExercises((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeManualExercise = (index: number) => {
    setManualExercises((prev) => prev.filter((_, i) => i !== index));
  };

  const addManualExercise = () => {
    setManualExercises((prev) => [
      ...prev,
      { name: "", sets: "", reps: "", rest: "", notes: "" } as any,
    ]);
  };

  const applyManualEdit = () => {
    const valid = manualExercises.filter((ex) => ex.name.trim());
    if (valid.length === 0) {
      toast.error("Ajoute au moins un exercice");
      return;
    }
    setAdaptedExercises(valid);
    setAdaptSummary("Modification manuelle appliquée");
    setAdaptMode(null);
    toast.success("Séance modifiée !");
  };

  const resetAdaptation = () => {
    setAdaptedExercises(null);
    setAdaptSummary(null);
    setAdaptMode(null);
    setConstraint("");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="ghost" className="text-xs" onClick={onBack}>
          <ArrowLeft className="h-3 w-3 mr-1" /> Retour
        </Button>
        <StartSessionButton
          session={adaptedExercises ? { ...session, exercises: adaptedExercises } : session}
          variant="default"
          label="Démarrer la séance"
        />
        <Button
          size="sm"
          variant="outline"
          className="text-xs gap-1"
          onClick={() => setAdaptMode(adaptMode ? null : "choose")}
        >
          <Wrench className="h-3 w-3" /> Modifier
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-xs gap-1"
          onClick={() => setDuplicateOpen(true)}
        >
          <Copy className="h-3 w-3" /> Dupliquer
        </Button>
        <SessionCalendarButton session={session} />
        {!isSimple && (
          <Button
            size="sm"
            variant="outline"
            className="text-xs gap-1"
            onClick={() => setAddToProgramOpen(true)}
          >
            <FolderPlus className="h-3 w-3" /> Programme
          </Button>
        )}
      </div>

      {/* Adaptation summary */}
      {adaptSummary && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 flex items-start gap-2">
          <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-primary font-medium">Séance adaptée</p>
            <p className="text-[11px] text-muted-foreground">{adaptSummary}</p>
          </div>
          <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-muted-foreground" onClick={resetAdaptation}>
            Restaurer
          </Button>
        </div>
      )}

      {/* Adapt mode chooser */}
      {adaptMode === "choose" && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <p className="text-xs font-medium text-foreground">Comment modifier la séance ?</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setAdaptMode("ai")}
              className="flex flex-col items-center gap-2 rounded-xl border border-border p-3 hover:border-primary/40 hover:bg-primary/5 transition-all"
            >
              <Brain className="h-5 w-5 text-primary" />
              <span className="text-[11px] font-medium text-foreground">Via le Cerveau</span>
              <span className="text-[9px] text-muted-foreground text-center">Décris ta contrainte, le Cerveau adapte</span>
            </button>
            <button
              onClick={startManualEdit}
              className="flex flex-col items-center gap-2 rounded-xl border border-border p-3 hover:border-primary/40 hover:bg-primary/5 transition-all"
            >
              <Pencil className="h-5 w-5 text-primary" />
              <span className="text-[11px] font-medium text-foreground">Manuellement</span>
              <span className="text-[9px] text-muted-foreground text-center">Modifier les exercices toi-même</span>
            </button>
          </div>
          <Button size="sm" variant="ghost" className="w-full text-xs text-muted-foreground" onClick={() => setAdaptMode(null)}>
            Annuler
          </Button>
        </div>
      )}

      {/* AI Adapt mode */}
      {adaptMode === "ai" && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-primary" />
            <p className="text-xs font-medium text-foreground">Adapter via le Cerveau</p>
          </div>
          <Textarea
            placeholder="Décris ta contrainte… (ex: j'ai mal au genou droit, je n'ai pas de barre aujourd'hui, je n'ai que 20 min…)"
            className="text-xs min-h-[60px]"
            value={constraint}
            onChange={(e) => setConstraint(e.target.value)}
          />
          <button
            onClick={() => setIsPermanent(!isPermanent)}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <div className={cn(
              "flex h-5 w-9 items-center rounded-full px-0.5 transition-colors",
              isPermanent ? "bg-primary" : "bg-muted"
            )}>
              <div className={cn(
                "h-4 w-4 rounded-full bg-card shadow-sm transition-transform",
                isPermanent ? "translate-x-4" : "translate-x-0"
              )} />
            </div>
            <span>{isPermanent ? "Contrainte permanente" : "Contrainte exceptionnelle"}</span>
          </button>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-xs flex-1" onClick={() => setAdaptMode("choose")}>
              Retour
            </Button>
            <Button size="sm" className="text-xs flex-1 gap-1" onClick={handleAdaptAI} disabled={aiLoading || !constraint.trim()}>
              {aiLoading ? <><Loader2 className="h-3 w-3 animate-spin" /> Adaptation…</> : <><Brain className="h-3 w-3" /> Adapter</>}
            </Button>
          </div>
        </div>
      )}

      {/* Manual edit mode */}
      {adaptMode === "manual" && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-primary" />
            <p className="text-xs font-medium text-foreground">Modification manuelle</p>
          </div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {manualExercises.map((ex, i) => (
              <div key={i} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-primary w-5">{i + 1}</span>
                  <Input
                    value={ex.name}
                    onChange={(e) => updateManualExercise(i, "name", e.target.value)}
                    placeholder="Nom de l'exercice"
                    className="h-7 text-xs flex-1"
                  />
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeManualExercise(i)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-1.5 ml-5">
                  <div>
                    <label className="text-[9px] text-muted-foreground">Séries</label>
                    <Input value={ex.sets || ""} onChange={(e) => updateManualExercise(i, "sets", e.target.value)} className="h-6 text-[10px]" />
                  </div>
                  <div>
                    <label className="text-[9px] text-muted-foreground">Reps</label>
                    <Input value={ex.reps || ""} onChange={(e) => updateManualExercise(i, "reps", e.target.value)} className="h-6 text-[10px]" />
                  </div>
                  <div>
                    <label className="text-[9px] text-muted-foreground">Repos</label>
                    <Input value={ex.rest || ""} onChange={(e) => updateManualExercise(i, "rest", e.target.value)} className="h-6 text-[10px]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" className="w-full text-xs" onClick={addManualExercise}>
            + Ajouter un exercice
          </Button>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-xs flex-1" onClick={() => setAdaptMode("choose")}>
              Retour
            </Button>
            <Button size="sm" className="text-xs flex-1 gap-1" onClick={applyManualEdit}>
              <Check className="h-3 w-3" /> Appliquer
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Dumbbell className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display font-bold text-foreground">{session.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{session.objective}</p>
          </div>
          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary shrink-0">
            {session.mode === "ai" ? <Brain className="h-3 w-3 mr-0.5" /> : null}
            {session.mode === "ai" ? "Généré par le Cerveau" : "Création manuelle"}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {session.duration_minutes} min</div>
          <Badge variant="outline" className="text-[10px]">{session.level_required}</Badge>
          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
            {session.fact_ids?.length || 0} fait(s)
          </Badge>
        </div>
      </div>

      {/* Warnings */}
      {session.warnings?.length > 0 && (
        <div className="space-y-2">
          {session.warnings.map((w, i) => {
            const { segments } = parseFactReferences(w);
            return (
              <div key={i} className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 px-4 py-2 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-400">{renderFactSegments(segments)}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Contraindications */}
      {session.contraindications?.length > 0 && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-destructive" />
            <p className="text-sm font-medium text-destructive">Contre-indications</p>
          </div>
          {session.contraindications.map((c: any, i: number) => (
            <p key={i} className="text-xs text-muted-foreground ml-6">
              • <span className="font-medium">{c.zone}</span> — {c.reason}
            </p>
          ))}
        </div>
      )}

      {/* Exercises */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Dumbbell className="h-4 w-4 text-primary" />
          Exercices ({displayExercises.length})
          {adaptedExercises && (
            <Badge variant="outline" className="text-[9px] border-primary/30 text-primary">Adaptée</Badge>
          )}
        </h3>
        {displayExercises.map((ex: any, i: number) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[10px] font-bold text-primary">
                {i + 1}
              </span>
              <h4 className="font-medium text-sm text-foreground">{ex.name}</h4>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground ml-9">
              {ex.sets && <span><span className="font-medium text-foreground">Séries :</span> {ex.sets}</span>}
              {ex.reps && <span><span className="font-medium text-foreground">Reps :</span> {ex.reps}</span>}
              {ex.rest && <span><span className="font-medium text-foreground">Repos :</span> {ex.rest}</span>}
            </div>
            {ex.notes && (
              <p className="text-xs text-muted-foreground mt-2 ml-9">
                <FactText text={ex.notes} />
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Tags */}
      {session.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {session.tags.map((tag: string) => (
            <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
          ))}
        </div>
      )}

      {/* Advice block — lightweight derived tips */}
      <SessionAdviceBlock session={session} />

      <AddToProgramDialog
        open={addToProgramOpen}
        onOpenChange={setAddToProgramOpen}
        sessionId={session.id}
        sessionTitle={session.title}
      />

      {duplicateOpen && (
        <DuplicateSessionDialog
          open={duplicateOpen}
          onOpenChange={(open) => !open && setDuplicateOpen(false)}
          session={session}
          onConfirm={async (title, exercises) => {
            setDuplicateLoading(true);
            await duplicateSession(session.id, title, exercises);
            setDuplicateLoading(false);
            setDuplicateOpen(false);
          }}
          loading={duplicateLoading}
        />
      )}
    </div>
  );
}
